import { SecureStorage as CapacitorSecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';

/** Onde o app está rodando: o mesmo código serve web, desktop (Electron) e celular (Capacitor). */
export type Platform = 'web' | 'desktop' | 'android' | 'ios';

/** Ponte exposta pelo preload do app desktop (apps/desktop/src/preload.ts). */
export type DesktopBridge = {
  platform: 'desktop';
  os: string;
  secureStore: {
    get(key: string): Promise<string | null>;
    set(key: string, value: string): Promise<void>;
    delete(key: string): Promise<void>;
  };
  openExternal(url: string): Promise<void>;
  /** Login pelo SSO no desktop (T-028b): retorno local (loopback) aberto pelo processo principal. */
  sso: {
    start(): Promise<string>;
    cancel(): Promise<void>;
    onResult(listener: (result: { code: string } | { error: string }) => void): () => void;
  };
};

declare global {
  interface Window {
    fluxusDesktop?: DesktopBridge;
  }
}

export function desktopBridge(): DesktopBridge | null {
  return typeof window !== 'undefined' && window.fluxusDesktop?.platform === 'desktop'
    ? window.fluxusDesktop
    : null;
}

export function detectPlatform(): Platform {
  if (desktopBridge()) return 'desktop';
  if (Capacitor.isNativePlatform()) {
    const native = Capacitor.getPlatform();
    if (native === 'android' || native === 'ios') return native;
  }
  return 'web';
}

/** Armazenamento de segredos (ex.: refresh token) protegido pelo sistema operacional. */
export type SecureStorage = {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
};

/**
 * Desktop: safeStorage do Electron (DPAPI no Windows). Android/iOS: Keystore/Keychain via plugin
 * nativo. Web: null — no navegador o segredo fica em cookie httpOnly, fora do alcance do JavaScript
 * (DEC-005); nunca em localStorage.
 */
export function getSecureStorage(platform: Platform = detectPlatform()): SecureStorage | null {
  if (platform === 'desktop') {
    const bridge = desktopBridge();
    if (!bridge) return null;
    return {
      get: (key) => bridge.secureStore.get(key),
      set: (key, value) => bridge.secureStore.set(key, value),
      remove: (key) => bridge.secureStore.delete(key),
    };
  }
  if (platform === 'android' || platform === 'ios') {
    // API de string pura (getItem/setItem): get()/set() convertem JSON e datas ISO, o que
    // transformaria um token com formato de data em Date.
    return {
      get: (key) => CapacitorSecureStorage.getItem(key),
      set: (key, value) => CapacitorSecureStorage.setItem(key, value),
      remove: (key) => CapacitorSecureStorage.removeItem(key),
    };
  }
  return null;
}

/** Como o app entrega um arquivo de texto: download (web e desktop) ou o compartilhar do sistema. */
export const textFileDelivery = (platform: Platform = detectPlatform()): 'download' | 'share' =>
  platform === 'android' || platform === 'ios' ? 'share' : 'download';

/**
 * Entrega um texto como arquivo. Web e desktop: download (no desktop, o Electron abre o "Salvar
 * como"). Android: o WebView não baixa arquivos `blob:` — abre o compartilhar do sistema, de onde a
 * pessoa salva no Drive, em notas ou envia a si mesma.
 */
export async function saveTextFile(opts: { fileName: string; title: string; text: string }): Promise<void> {
  if (textFileDelivery() === 'share') {
    const { Share } = await import('@capacitor/share');
    await Share.share({ title: opts.title, text: opts.text, dialogTitle: opts.title });
    return;
  }
  downloadBlob(new Blob([opts.text], { type: 'text/plain;charset=utf-8' }), opts.fileName);
}

/** Web e desktop: baixa o conteúdo com o nome pedido (no desktop, o Electron abre o "Salvar como"). */
function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Libera o endereço depois que o navegador começou o download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Base64 do conteúdo (para gravar no armazenamento do app no Android). */
async function blobBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  // Em blocos: String.fromCharCode com o arquivo inteiro estoura a pilha em arquivos grandes.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/** O que o Android usa para entregar um arquivo: gravar no cache do app e abrir o compartilhar. */
export type NativeFiles = {
  /** Grava (base64) no cache do app; devolve o endereço do arquivo. */
  writeCache(path: string, base64: string): Promise<string>;
  share(opts: { title: string; files: string[]; dialogTitle: string }): Promise<unknown>;
};

const capacitorFiles = async (): Promise<NativeFiles> => {
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
  ]);
  return {
    writeCache: async (path, data) =>
      (await Filesystem.writeFile({ path, data, directory: Directory.Cache })).uri,
    share: (opts) => Share.share(opts),
  };
};

/** Fechar o compartilhar sem escolher nada (o plugin rejeita com "Share canceled") não é erro. */
export const isShareCanceled = (err: unknown) => err instanceof Error && /cancel/i.test(err.message);

/**
 * Entrega um arquivo binário (ex.: o PDF da ata). Web e desktop: download. Android: o WebView não baixa `blob:` —
 * grava o arquivo no cache do app e abre o compartilhar do sistema com ele (salvar, abrir num leitor, enviar).
 */
export async function saveBlobFile(
  opts: { fileName: string; title: string; blob: Blob },
  env: { platform?: Platform; native?: () => Promise<NativeFiles> } = {},
): Promise<void> {
  if (textFileDelivery(env.platform ?? detectPlatform()) === 'share') {
    const native = await (env.native ?? capacitorFiles)();
    const uri = await native.writeCache(opts.fileName, await blobBase64(opts.blob));
    try {
      await native.share({ title: opts.title, files: [uri], dialogTitle: opts.title });
    } catch (err) {
      if (isShareCanceled(err)) return;
      throw err;
    }
    return;
  }
  downloadBlob(opts.blob, opts.fileName);
}
